// ProximityToast — toast emergente que aparece cuando estás cerca de:
//  · un venue de un sabor típico (gastronomía)
//  · un lugar destacado del viaje (sinagogas, miradores, memoriales)
// Botones: Llévame · Ahora no · Hoy no.

import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Navigation, MapPin } from "lucide-react";
import { useGeolocation } from "../hooks/useGeolocation";
import { useProximityAlerts } from "../hooks/useProximityAlerts";
import { TRIP } from "../data/trip";
import { formatDistance } from "../hooks/useGeolocation";

// Tipos de places que merecen alerta de proximidad (los más significativos)
const HIGHLIGHT_TYPES = new Set(["synagogue", "memorial"]);

// Aplana tastings.venues + places destacados en items para el hook
function buildAlertItems() {
  const items = [];

  // 1) Sabores típicos (venues por restaurante)
  const tastings = TRIP.tastings || {};
  for (const city of Object.keys(tastings)) {
    for (const t of tastings[city]) {
      if (!t.venues) continue;
      for (const v of t.venues) {
        items.push({
          kind: "tasting",
          tastingId: t.id,
          tastingName: t.name,
          tastingEmoji: t.emoji,
          city,
          tip: t.tip,
          venue: v,
        });
      }
    }
  }

  // 2) Lugares destacados del viaje (sinagogas + memoriales)
  const places = TRIP.places || {};
  for (const id of Object.keys(places)) {
    const p = places[id];
    if (!p || !p.lat || !p.lng) continue;
    if (!HIGHLIGHT_TYPES.has(p.type) && !p.jewish) continue;
    items.push({
      kind: "place",
      tastingId: `place_${p.id}`, // reaprovecho la key del cooldown
      tastingName: p.name,
      tastingEmoji: p.icon || (p.type === "synagogue" ? "🕍" : "🕯️"),
      city: p.city,
      placeId: p.id,
      venue: { name: p.name, lat: p.lat, lng: p.lng },
    });
  }

  return items;
}

export default function ProximityToast() {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const base = tripId ? `/viaje/${tripId}` : "";
  const { position } = useGeolocation();
  const items = buildAlertItems();
  const { activeAlert, dismiss, snoozeToday } = useProximityAlerts({
    position,
    items,
    radius: 200,
    cooldownMs: 60 * 60 * 1000,
  });

  const isTasting = activeAlert?.kind === "tasting";
  const isPlace = activeAlert?.kind === "place";

  const handleYes = () => {
    if (!activeAlert) return;
    const { venue } = activeAlert;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${venue.lat},${venue.lng}&travelmode=walking`;
    window.open(url, "_blank");
    dismiss();
    if (isTasting) {
      navigate(`${base}/sabores?city=${encodeURIComponent(activeAlert.city)}&focus=${activeAlert.tastingId}`);
    } else if (isPlace) {
      navigate(`${base}/lugar/${activeAlert.placeId}`);
    }
  };

  const handleDetails = () => {
    if (!isPlace) return;
    dismiss();
    navigate(`${base}/lugar/${activeAlert.placeId}`);
  };

  return (
    <AnimatePresence>
      {activeAlert && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: "spring", stiffness: 320, damping: 30 }}
          className="fixed left-3 right-3 z-[1000]"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)" }}
          data-testid="proximity-toast"
        >
          <div className="card-paper p-4 shadow-2xl border-2 border-[hsl(var(--accent))]/40">
            <button
              onClick={dismiss}
              className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-[hsl(var(--muted))] hover:text-[hsl(var(--ink))]"
              aria-label="Cerrar"
              data-testid="proximity-toast-close"
            >
              <X size={16} />
            </button>

            <div className="flex items-start gap-3 mb-2 pr-6">
              <span className="text-3xl shrink-0">{activeAlert.tastingEmoji}</span>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))]">
                  {isTasting ? "¡Estás cerca!" : "✡ Lugar cercano"}
                </p>
                <h3 className="text-base font-bold leading-tight" data-testid="proximity-toast-title">
                  {isTasting
                    ? `No olvides probar ${activeAlert.tastingName}`
                    : activeAlert.tastingName}
                </h3>
                <p className="text-xs text-[hsl(var(--muted))] mt-1 flex items-center gap-1">
                  <MapPin size={12} />
                  {isTasting && `${activeAlert.venue.name} · `}
                  {formatDistance(activeAlert.distance)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-3">
              <button
                onClick={handleYes}
                className="col-span-3 sm:col-span-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-[hsl(var(--ink))] text-[hsl(var(--bg))] text-sm font-semibold active:scale-95"
                data-testid="proximity-toast-yes"
              >
                <Navigation size={14} /> {isPlace ? "Ir al lugar" : "Llévame allí"}
              </button>
              <button
                onClick={isPlace ? handleDetails : dismiss}
                className="py-2 px-3 rounded-xl bg-[hsl(var(--bg))] border border-[hsl(var(--line))] text-xs font-medium text-[hsl(var(--ink-soft))] active:scale-95"
                data-testid="proximity-toast-later"
              >
                {isPlace ? "Ver detalle" : "Ahora no"}
              </button>
              <button
                onClick={snoozeToday}
                className="py-2 px-3 rounded-xl bg-[hsl(var(--bg))] border border-[hsl(var(--line))] text-xs font-medium text-[hsl(var(--muted))] active:scale-95"
                data-testid="proximity-toast-snooze"
              >
                Hoy no
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
